/**
 * MED-781: Derive the sorted Needs Attention rail items from the dashboard's
 * raw data sources.
 *
 * - "Today" is anchored to the coach's local timezone (AC9).
 * - Starts Today vs. Prep Needed swap at T-60 min per Comment 3.
 * - Sort order follows AC8 / NEEDS_ATTENTION_TYPE_PRIORITY.
 * - The rail re-renders every minute so countdowns and the same-day swap stay
 *   fresh without websockets (AC9 — non-stale countdown).
 */

import { useEffect, useMemo, useState } from 'react';
import type {
  CoachUpcomingSession,
  CoachRecentCompletedSession,
} from '../../../services/api/coachPortal';
import type { ActionItemDescriptor } from './useCoachActionItems';
import type {
  NeedsAttentionItem,
  StartsTodayItem,
  OverdueAttendanceItem,
  PostSessionFeedbackItem,
  PrepNeededItem,
  SessionReportReadyItem,
} from '../components/dashboard/needsAttentionTypes';
import { NEEDS_ATTENTION_TYPE_PRIORITY } from '../components/dashboard/needsAttentionTypes';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { composeCoachSessionLabel } from '../utils/sessionLabel';

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const STARTS_TODAY_THRESHOLD_MS = 60 * MS_PER_MINUTE;

interface UseNeedsAttentionItemsParams {
  upcomingSessions: CoachUpcomingSession[];
  recentCompletedSession: CoachRecentCompletedSession | null;
  /**
   * Pending overdue attendance + Calendly + Midpoint/Endpoint feedback prompts
   * — already aggregated by `useCoachActionItems` (extracted from the legacy
   * ActionItemsPanel logic).
   */
  actionItems: ActionItemDescriptor[];
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildStartsTodayItems(
  sessions: CoachUpcomingSession[],
  now: Date,
): StartsTodayItem[] {
  return sessions
    .filter(s => isSameLocalDay(new Date(s.scheduled_start), now))
    .map<StartsTodayItem>(s => ({
      id: `starts-today-${s.id}`,
      type: 'starts_today',
      cohortId: s.cohort_id,
      sessionId: s.id,
      cohortName: s.cohort_name,
      // MED-1106: one shared label rule.
      sessionTitle: composeCoachSessionLabel({
        session_number: s.session_number,
        topic_name: s.topic_name,
        group_name: s.group_name,
        title: s.title,
      }),
      scheduledStart: s.scheduled_start,
      participantCount: s.participant_count,
      groupName: s.group_name,
      status: s.status,
      // MED-1147: internal-awareness divergence data.
      participantDisplayName: s.topic_display_name,
      participantDisplayDiffers: s.participant_display_differs,
    }))
    .sort(
      (a, b) =>
        new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime(),
    );
}

function buildPrepNeededItem(
  sessions: CoachUpcomingSession[],
  now: Date,
): PrepNeededItem | null {
  // AC11: Prep Needed appears for the next upcoming session if not yet "prepped".
  // For v1 (per Q1 resolution) all sessions are not-prepped.
  // MDF-244: the list also holds in-progress sessions and scheduled sessions
  // past their start time (until their estimated end). Those are the Starts
  // Today card's job; Prep Needed is for the next session that has not started.
  const next = sessions.find(
    s => s.status !== 'in_progress' && new Date(s.scheduled_start) > now,
  );
  if (!next) return null;
  const start = new Date(next.scheduled_start).getTime();
  const msUntilStart = start - now.getTime();

  // Comment 3: at T-60 min, Prep Needed is replaced by Starts Today. Skip emitting it.
  if (msUntilStart <= STARTS_TODAY_THRESHOLD_MS) {
    return null;
  }

  const isToday = isSameLocalDay(new Date(next.scheduled_start), now);
  return {
    id: `prep-needed-${next.id}`,
    type: 'prep_needed',
    cohortId: next.cohort_id,
    sessionId: next.id,
    cohortName: next.cohort_name,
    // MED-1106: one shared label rule.
    sessionTitle: composeCoachSessionLabel({
      session_number: next.session_number,
      topic_name: next.topic_name,
      group_name: next.group_name,
      title: next.title,
    }),
    scheduledStart: next.scheduled_start,
    participantCount: next.participant_count,
    groupName: next.group_name,
    isSameDayUrgent: isToday,
    // MED-1147: internal-awareness divergence data.
    participantDisplayName: next.topic_display_name,
    participantDisplayDiffers: next.participant_display_differs,
  };
}

function buildSessionReportReadyItem(
  recent: CoachRecentCompletedSession | null,
): SessionReportReadyItem | null {
  if (!recent) return null;
  return {
    id: `session-report-${recent.id}`,
    type: 'session_report_ready',
    sessionId: recent.id,
    cohortId: recent.cohort_id,
    cohortName: recent.cohort_name,
    // MED-1106: one shared label rule — the resolved topic (AC-C2 backend fix)
    // now lives in the composed label, so the card no longer carries topicName
    // as a separate field.
    sessionTitle: composeCoachSessionLabel({
      session_number: recent.session_number,
      topic_name: recent.topic_name,
      group_name: recent.group_name,
      title: recent.title,
    }),
    scheduledEnd: recent.scheduled_end,
    attendanceMarkedAt: recent.attendance_marked_at,
    // MED-1147: internal-awareness divergence data.
    participantDisplayName: recent.topic_display_name,
    participantDisplayDiffers: recent.participant_display_differs,
  };
}

function buildOverdueAttendanceItems(
  actionItems: ActionItemDescriptor[],
  now: Date,
): OverdueAttendanceItem[] {
  return actionItems
    .filter(item => item.type === 'overdue_attendance')
    .map<OverdueAttendanceItem>(item => {
      const scheduledEnd = item.scheduledEnd ?? item.referenceDate ?? now.toISOString();
      const elapsedMs = now.getTime() - new Date(scheduledEnd).getTime();
      const daysOverdue = Math.max(0, Math.floor(elapsedMs / MS_PER_DAY));
      return {
        id: `overdue-${item.sessionId ?? item.cohortId ?? item.id}`,
        type: 'overdue_attendance',
        cohortId: item.cohortId ?? '',
        sessionId: item.sessionId ?? '',
        cohortName: item.cohortName ?? '',
        sessionTitle: item.label,
        groupName: item.groupName ?? '',
        scheduledEnd,
        daysOverdue,
        // MED-1147: internal-awareness divergence data.
        participantDisplayName: item.participantDisplayName ?? null,
        participantDisplayDiffers: item.participantDisplayDiffers ?? false,
      };
    })
    .sort((a, b) => b.daysOverdue - a.daysOverdue);
}

function buildPostSessionFeedbackItems(
  actionItems: ActionItemDescriptor[],
  now: Date,
): PostSessionFeedbackItem[] {
  // MED-867 AC4: no grace/threshold — elapsed time is derived the same way as
  // Overdue Attendance's daysOverdue, surfaced as "Pending N days".
  return actionItems
    .filter(item => item.type === 'post_session_feedback')
    .map<PostSessionFeedbackItem>(item => {
      const scheduledEnd = item.scheduledEnd ?? item.referenceDate ?? now.toISOString();
      const elapsedMs = now.getTime() - new Date(scheduledEnd).getTime();
      const daysPending = Math.max(0, Math.floor(elapsedMs / MS_PER_DAY));
      return {
        id: `feedback-pending-${item.sessionId ?? item.id}`,
        type: 'post_session_feedback',
        cohortId: item.cohortId ?? '',
        sessionId: item.sessionId ?? '',
        cohortName: item.cohortName ?? '',
        sessionTitle: item.label,
        groupName: item.groupName ?? '',
        scheduledEnd,
        daysPending,
        // MED-1147: internal-awareness divergence data.
        participantDisplayName: item.participantDisplayName ?? null,
        participantDisplayDiffers: item.participantDisplayDiffers ?? false,
      };
    })
    .sort((a, b) => b.daysPending - a.daysPending);
}

function buildOtherItems(
  actionItems: ActionItemDescriptor[],
): NeedsAttentionItem[] {
  const out: NeedsAttentionItem[] = [];
  for (const item of actionItems) {
    if (item.type === 'connect_calendly') {
      out.push({ id: 'connect-calendly', type: 'connect_calendly' });
    } else if (item.type === 'calendly_booking_blocked') {
      out.push({
        id: item.id,
        type: 'calendly_booking_blocked',
        problem: item.calendlyProblem ?? 'required_question',
        eventTypeName: item.eventTypeName ?? null,
      });
    } else if (item.type === 'meeting_link_missing' && item.meetingLink) {
      out.push({ id: item.id, type: 'meeting_link_missing', link: item.meetingLink });
    } else if (item.type === 'calendly_20min_unmapped') {
      out.push({
        id: item.id,
        type: 'calendly_20min_unmapped',
        planStage: item.planStage ?? null,
        offTwentyNames: item.offTwentyNames ?? [],
      });
    } else if (item.type === 'feedback') {
      out.push({
        id: `feedback-${item.cohortId}-${item.surveyType}`,
        type: 'feedback',
        cohortId: item.cohortId ?? '',
        cohortName: item.cohortName ?? '',
        surveyType: item.surveyType ?? 'midpoint',
      });
    }
  }
  return out;
}

export function useNeedsAttentionItems({
  upcomingSessions,
  recentCompletedSession,
  actionItems,
}: UseNeedsAttentionItemsParams): NeedsAttentionItem[] {
  // Re-render every minute to keep the same-day swap and "Starts in X min"
  // countdown values fresh (AC9). Cheap because the derivation is pure.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), MS_PER_MINUTE);
    return () => clearInterval(interval);
  }, []);

  // MED-890 / AC2: Suppress the "Prep Needed" SGOS dashboard CTA card while the
  // live_session_delivery flag is off (or still loading — useFeatureFlag fails
  // closed). The card is the only dashboard entry point into the Session Prep
  // flow, so omitting it here keeps the dashboard free of dead-end CTAs.
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');

  return useMemo(() => {
    void tick;
    const now = new Date();

    // MED-1181 / AC2: every LSD dashboard card (Starts Today, Prep Needed,
    // Session Report Ready) is gated on BOTH the global flag AND the cohort's
    // per-modality Live Session Delivery toggle. Filter the session sources to
    // LSD-enabled cohorts (empty/null when the flag is off — fails closed) so a
    // classic MED01 cohort's session never produces a card. Starts Today and
    // Session Report Ready were previously ungated entirely; this adds the full
    // flag + modality condition from scratch.
    // MED-1350: a check-in carries the group's id, but it is a 1:1. It has no
    // live group session to prep or start, so it never makes an LSD card.
    const lsdUpcomingSessions = liveSessionDeliveryEnabled
      ? upcomingSessions.filter(
          s => s.live_session_delivery_enabled && s.session_type !== 'one_on_one_session',
        )
      : [];
    const lsdRecentCompleted =
      liveSessionDeliveryEnabled &&
      recentCompletedSession?.live_session_delivery_enabled
        ? recentCompletedSession
        : null;

    const startsToday = buildStartsTodayItems(lsdUpcomingSessions, now);
    const prepNeeded = buildPrepNeededItem(lsdUpcomingSessions, now);
    const reportReady = buildSessionReportReadyItem(lsdRecentCompleted);
    const overdue = buildOverdueAttendanceItems(actionItems, now);
    // MED-867 AC6/AC12: only surface the feedback cards when the flag is on.
    // MED-1181: the per-cohort LSD gate is enforced upstream in
    // useCoachActionItems (post_session_feedback descriptors are built only for
    // LSD-enabled cohorts), so the descriptors reaching here are already
    // per-cohort filtered; the flag check remains as defense in depth.
    const postSessionFeedback = liveSessionDeliveryEnabled
      ? buildPostSessionFeedbackItems(actionItems, now)
      : [];
    const others = buildOtherItems(actionItems);

    const all: NeedsAttentionItem[] = [
      ...startsToday,
      ...overdue,
      ...postSessionFeedback,
      ...(prepNeeded ? [prepNeeded] : []),
      ...(reportReady ? [reportReady] : []),
      ...others,
    ];

    return all.sort((a, b) => {
      const pa = NEEDS_ATTENTION_TYPE_PRIORITY[a.type];
      const pb = NEEDS_ATTENTION_TYPE_PRIORITY[b.type];
      if (pa !== pb) return pa - pb;
      // Within feedback group, midpoint before endpoint (AC8).
      if (a.type === 'feedback' && b.type === 'feedback') {
        if (a.surveyType !== b.surveyType) {
          return a.surveyType === 'midpoint' ? -1 : 1;
        }
      }
      return 0;
    });
  }, [tick, upcomingSessions, recentCompletedSession, actionItems, liveSessionDeliveryEnabled]);
}
